import { inject, provide } from 'vue';
/**
 * A symbol rather than a string key, so nothing else can collide with it and
 * nothing can inject it by guessing.
 */
const AUTH_SESSION = Symbol('hb.auth-session');
/**
 * Makes one session available to a component tree. Call it once, in the root
 * component's `setup`, with the session the entry point built.
 *
 * Provide/inject rather than a module-level singleton: a singleton is shared by
 * every app instance in a process, which means one test's signed-in user leaks
 * into the next test in the same file, and a future SSR pass would serve one
 * user's session to another.
 */
export function provideAuthSession(session) {
    provide(AUTH_SESSION, session);
    return session;
}
/**
 * The same thing from outside a component, for an entry point that builds the
 * session before it has a root component to put it in.
 *
 * An application-level provide is inherited by every component in that app, so
 * `useAuthSession()` finds it exactly as it would a parent's.
 */
export function installAuthSession(app, session) {
    app.provide(AUTH_SESSION, session);
    return session;
}
/** The provided session. Throws rather than returning undefined, so a missing provider is a loud failure at first render. */
export function useAuthSession() {
    const session = inject(AUTH_SESSION, null);
    if (session === null) {
        throw new Error('No auth session was provided. Call provideAuthSession() in the root component before any component calls useAuthSession().');
    }
    return session;
}
//# sourceMappingURL=inject.js.map